import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { FilterValues } from "../data-model/filterValues";

/**
 * Local (non-Redux) store for applied filter values keyed by table/filter name.
 * Replaces medonc's `filterValues` Redux slice for RisaCustomTable.
 */
export type FilterValuesContextType = {
  filters: FilterValues[];
  getFilterByName: (name: string) => FilterValues | undefined;
  setFilter: (data: FilterValues) => void;
  clearFilter: (name: string) => void;
};

const FilterValuesContext = createContext<FilterValuesContextType>({
  filters: [],
  getFilterByName: () => undefined,
  setFilter: () => {},
  clearFilter: () => {},
});

type FilterValuesProviderProps = {
  children: ReactNode;
};

/**
 * Provides applied-filter state for FilterButton / FilterTag / FilterBody.
 *
 * @param props - Provider children
 * @returns Context provider wrapping children
 */
export const FilterValuesProvider: React.FC<FilterValuesProviderProps> = ({
  children,
}) => {
  const [filters, setFilters] = useState<FilterValues[]>([]);

  const getFilterByName = useCallback(
    (name: string) => filters.find((filter) => filter.name === name),
    [filters],
  );

  const setFilter = useCallback((data: FilterValues) => {
    setFilters((prev) => {
      const index = prev.findIndex((filter) => filter.name === data.name);
      if (index === -1) {
        return [...prev, data];
      }
      const next = [...prev];
      next[index] = data;
      return next;
    });
  }, []);

  const clearFilter = useCallback((name: string) => {
    setFilters((prev) => prev.filter((filter) => filter.name !== name));
  }, []);

  const value = useMemo(
    () => ({ filters, getFilterByName, setFilter, clearFilter }),
    [filters, getFilterByName, setFilter, clearFilter],
  );

  return (
    <FilterValuesContext.Provider value={value}>
      {children}
    </FilterValuesContext.Provider>
  );
};

/**
 * Hook to read/write applied filter values for RisaCustomTable.
 *
 * @returns Filter values context API
 */
export const useFilterValues = () => useContext(FilterValuesContext);
